import * as React from 'react';
import { createPortal } from 'react-dom';
import type { Locale } from 'date-fns';
import { Filter, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react';
import { cn } from '../../utils';
import { Button } from '../inputs/button';
import { DateRangePicker } from '../inputs/date-range-picker';
import type { DateRangeValue } from '../inputs/date-range-picker';
import { Input } from '../inputs/input';
import { MultiSelect } from '../inputs/multi-select';
import { Select } from '../inputs/select';
import type { SelectOption } from '../inputs/select';
import { Drawer } from '../navigation/drawer';

export type DataTableSortDirection = 'asc' | 'desc';
export type DataTableNumberOperator =
  'lt' | 'lte' | 'eq' | 'neq' | 'gte' | 'gt';

export interface DataTableNumberCondition {
  operator: DataTableNumberOperator;
  value: number;
}

export type DataTableFilterValue =
  | { type: 'text'; value: string }
  | { type: 'date-range'; from?: string; to?: string }
  | { type: 'multi-select'; values: string[] }
  | {
      type: 'number';
      first: DataTableNumberCondition;
      join?: 'and' | 'or';
      second?: DataTableNumberCondition;
    };

export type DataTableFilterState = Record<string, DataTableFilterValue>;

interface FilterDefinitionBase {
  formatValue?: (value: DataTableFilterValue) => React.ReactNode;
}

export type DataTableFilterDefinition = FilterDefinitionBase &
  (
    | { type: 'text'; placeholder?: string }
    | {
        type: 'date-range';
        locale?: Locale;
        placeholderFrom?: string;
        placeholderTo?: string;
      }
    | {
        type: 'multi-select';
        options: SelectOption[];
        placeholder?: string;
        searchable?: boolean;
      }
    | { type: 'number'; step?: number; placeholder?: string }
  );

export interface DataTableColumnDef<K extends string = string> {
  /** Stable column id; inferred from `key` or the column position when omitted. */
  id?: string;
  /** Sort key retained for compatibility with the app's existing header definitions. */
  key?: K | null;
  label: React.ReactNode;
  sortKey?: K;
  align?: 'left' | 'right' | 'center';
  width?: string;
  filter?: DataTableFilterDefinition;
}

/**
 * A column that knows how to render its own cells.
 *
 * The render-facing superset of `DataTableColumnDef` (which `DataTableFilterBar`
 * still reads on its own). Collapsing the header, the cells and the widths into
 * one array is what makes reordering possible at all: while the order lived in
 * three parallel places — one of them positional JSX — there was nothing to
 * permute.
 */
export interface DataTableColumn<T, K extends string = string> extends Omit<
  DataTableColumnDef<K>,
  'width' | 'id' | 'key'
> {
  /**
   * Stable and hand-written. It is persisted to `localStorage`, so never derive
   * it from the column's position — renaming it silently discards the reader's
   * saved layout, and reusing one across two tables mixes them up.
   */
  id: string;
  /** Plain-text label for aria-labels; falls back to `label` when it is a string. */
  labelText?: string;

  /** `'w-36'` is accepted for migration and normalised once; new code writes numbers. */
  width?: number | string;
  minWidth?: number;
  maxWidth?: number;

  /** Never moves, never resizes, gets no grip. Must sit at the matching extreme. */
  pinned?: 'start' | 'end';
  resizable?: boolean;
  reorderable?: boolean;

  cell: (item: T) => React.ReactNode;
  /** Extra classes on the `<td>`. */
  cellClassName?: string | ((item: T) => string | undefined);
  /** Extra props on the `<td>` — this is where an `onClick` that stops
   * propagation belongs. */
  cellProps?: (item: T) => React.TdHTMLAttributes<HTMLTableCellElement>;

  /** `false` emits `data-truncate="off"`: no clipping, and no tooltip. */
  truncate?: boolean;
  /** Replaces the `...(canWrite ? [column] : [])` spread at the call sites. */
  hidden?: boolean;
}

export interface DataTableFilterLabels {
  filters: string;
  noFilters: string;
  activeFilters: (count: number) => string;
  filterColumn: (column: string) => string;
  apply: string;
  cancel: string;
  clear: string;
  clearAll: string;
  close: string;
  addCondition: string;
  removeCondition: string;
  and: string;
  or: string;
  selected: (count: number) => string;
  searchPlaceholder: string;
  selectPlaceholder: string;
  noResults: string;
  operators: Record<DataTableNumberOperator, string>;
}

/**
 * All the filter bar reads off a column — so it accepts both `DataTableColumnDef`
 * (legacy headers) and `DataTableColumn` (whose `width` is a number).
 */
export type DataTableFilterableColumn<K extends string = string> = Pick<
  DataTableColumnDef<K>,
  'id' | 'key' | 'label' | 'filter'
> & {
  hidden?: boolean;
};

export interface DataTableFilterBarProps<K extends string = string> {
  columns: DataTableFilterableColumn<K>[];
  filters: DataTableFilterState;
  onFiltersChange: (filters: DataTableFilterState) => void;
  labels: DataTableFilterLabels;
}

export function DataTableFilterBar<K extends string>({
  columns,
  filters,
  onFiltersChange,
  labels,
}: DataTableFilterBarProps<K>) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const filterColumns = columns.flatMap(
    (
      column,
      index,
    ): Array<
      DataTableFilterableColumn<K> & {
        filter: DataTableFilterDefinition;
        columnId: string;
      }
    > =>
      column.filter && !column.hidden
        ? [
            {
              ...column,
              filter: column.filter,
              columnId: String(column.id ?? column.key ?? index),
            },
          ]
        : [],
  );
  const activeColumns = filterColumns.filter(
    (column) => filters[column.columnId],
  );

  function clear(columnId: string) {
    const next = { ...filters };
    delete next[columnId];
    onFiltersChange(next);
  }

  return (
    <>
      <div className="flex min-h-9 items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="md:hidden"
          onClick={() => setDrawerOpen(true)}
        >
          <SlidersHorizontal />
          {activeColumns.length > 0
            ? labels.activeFilters(activeColumns.length)
            : labels.filters}
        </Button>

        <div className="hidden min-w-0 flex-1 items-center gap-2 md:flex">
          {activeColumns.length === 0 ? (
            <span className="text-sm text-muted-foreground">
              {labels.noFilters}
            </span>
          ) : (
            <>
              <Filter className="size-4 shrink-0 text-riso-blue" />
              <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto py-1">
                {activeColumns.map((column) => (
                  <span
                    key={column.columnId}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-riso-blue/25 bg-riso-blue-light px-2.5 py-1 text-xs font-semibold text-foreground"
                  >
                    <span>{column.label}:</span>
                    <span className="font-normal">
                      {formatFilterValue(
                        column.filter,
                        filters[column.columnId],
                        labels,
                      )}
                    </span>
                    <button
                      type="button"
                      aria-label={labels.clear}
                      className="rounded-full p-0.5 hover:bg-riso-blue/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => clear(column.columnId)}
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => onFiltersChange({})}
              >
                {labels.clearAll}
              </Button>
            </>
          )}
        </div>
      </div>

      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        width={320}
        swipeable={false}
        aria-label={labels.filters}
        className="overflow-y-auto p-4"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold">{labels.filters}</h2>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label={labels.close}
            onClick={() => setDrawerOpen(false)}
          >
            <X />
          </Button>
        </div>
        <div className="space-y-4">
          {filterColumns.map((column) => (
            <section
              key={`${column.columnId}:${JSON.stringify(filters[column.columnId])}`}
              className="rounded-xl border-2 border-border p-3"
            >
              <h3 className="mb-3 text-sm font-semibold">{column.label}</h3>
              <ColumnFilterEditor
                definition={column.filter}
                value={filters[column.columnId]}
                labels={labels}
                onApply={(value) =>
                  onFiltersChange({ ...filters, [column.columnId]: value })
                }
                onClear={() => clear(column.columnId)}
              />
            </section>
          ))}
        </div>
        {activeColumns.length > 0 && (
          <Button
            type="button"
            variant="outline"
            className="mt-4 w-full"
            onClick={() => onFiltersChange({})}
          >
            <Trash2 />
            {labels.clearAll}
          </Button>
        )}
      </Drawer>
    </>
  );
}

export interface FilterPopoverProps {
  column: { label: React.ReactNode; filter: DataTableFilterDefinition };
  value?: DataTableFilterValue;
  labels: DataTableFilterLabels;
  labelText: string;
  /**
   * Any change closes the panel. The panel is positioned once, against a `<th>`
   * that a column resize or reorder is about to move out from under it.
   */
  closeToken?: number;
  onApply: (value: DataTableFilterValue) => void;
  onClear: () => void;
}

export function FilterPopover({
  column,
  value,
  labels,
  labelText,
  closeToken,
  onApply,
  onClear,
}: FilterPopoverProps) {
  const [open, setOpen] = React.useState(false);
  // Adjusted during render rather than in an effect: closing in an effect would
  // paint the panel once at its stale position before removing it.
  const [seenCloseToken, setSeenCloseToken] = React.useState(closeToken);
  if (closeToken !== seenCloseToken) {
    setSeenCloseToken(closeToken);
    setOpen(false);
  }
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [style, setStyle] = React.useState<React.CSSProperties>({
    position: 'fixed',
    visibility: 'hidden',
  });

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = column.filter.type === 'date-range' ? 448 : 320;
    const left = Math.min(
      Math.max(8, rect.left),
      Math.max(8, window.innerWidth - width - 8),
    );
    setStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left,
      width,
      maxWidth: 'calc(100vw - 16px)',
      zIndex: 80,
    });
  }, [column.filter.type, open]);

  React.useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-ui-popover]')) return;
      if (
        !triggerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={labels.filterColumn(labelText)}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          value && 'bg-riso-blue-light text-riso-blue',
        )}
      >
        <Filter className="size-3.5" />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            data-ui-popover
            style={style}
            className="animate-fade-in rounded-xl border-2 border-border bg-card p-4 text-left shadow-hard"
          >
            <h3 className="mb-3 font-display text-sm font-bold">
              {column.label}
            </h3>
            <ColumnFilterEditor
              key={JSON.stringify(value)}
              definition={column.filter}
              value={value}
              labels={labels}
              onApply={(next) => {
                onApply(next);
                setOpen(false);
              }}
              onClear={() => {
                onClear();
                setOpen(false);
              }}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

function ColumnFilterEditor({
  definition,
  value,
  labels,
  onApply,
  onClear,
}: {
  definition: DataTableFilterDefinition;
  value?: DataTableFilterValue;
  labels: DataTableFilterLabels;
  onApply: (value: DataTableFilterValue) => void;
  onClear: () => void;
}) {
  switch (definition.type) {
    case 'text':
      return (
        <TextFilterEditor
          definition={definition}
          value={value?.type === 'text' ? value : undefined}
          labels={labels}
          onApply={onApply}
          onClear={onClear}
        />
      );
    case 'date-range':
      return (
        <DateFilterEditor
          definition={definition}
          value={value?.type === 'date-range' ? value : undefined}
          labels={labels}
          onApply={onApply}
          onClear={onClear}
        />
      );
    case 'multi-select':
      return (
        <MultiSelectFilterEditor
          definition={definition}
          value={value?.type === 'multi-select' ? value : undefined}
          labels={labels}
          onApply={onApply}
          onClear={onClear}
        />
      );
    case 'number':
      return (
        <NumberFilterEditor
          definition={definition}
          value={value?.type === 'number' ? value : undefined}
          labels={labels}
          onApply={onApply}
          onClear={onClear}
        />
      );
  }
}

function EditorActions({
  labels,
  canApply,
  hasValue,
  onApply,
  onClear,
}: {
  labels: DataTableFilterLabels;
  canApply: boolean;
  hasValue: boolean;
  onApply: () => void;
  onClear: () => void;
}) {
  return (
    <div className="mt-3 flex justify-end gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!hasValue}
        onClick={onClear}
      >
        {labels.clear}
      </Button>
      <Button type="button" size="sm" disabled={!canApply} onClick={onApply}>
        {labels.apply}
      </Button>
    </div>
  );
}

function TextFilterEditor({
  definition,
  value,
  labels,
  onApply,
  onClear,
}: {
  definition: DataTableFilterDefinition & { type: 'text' };
  value?: Extract<DataTableFilterValue, { type: 'text' }>;
  labels: DataTableFilterLabels;
  onApply: (value: DataTableFilterValue) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = React.useState(value?.value ?? '');
  const normalized = draft.trim();
  return (
    <>
      <Input
        size="sm"
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && normalized) {
            onApply({ type: 'text', value: normalized });
          }
        }}
        placeholder={definition.placeholder ?? labels.searchPlaceholder}
      />
      <EditorActions
        labels={labels}
        canApply={Boolean(normalized)}
        hasValue={Boolean(value)}
        onApply={() => onApply({ type: 'text', value: normalized })}
        onClear={onClear}
      />
    </>
  );
}

function DateFilterEditor({
  definition,
  value,
  labels,
  onApply,
  onClear,
}: {
  definition: DataTableFilterDefinition & { type: 'date-range' };
  value?: Extract<DataTableFilterValue, { type: 'date-range' }>;
  labels: DataTableFilterLabels;
  onApply: (value: DataTableFilterValue) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = React.useState<DateRangeValue>({
    from: value?.from,
    to: value?.to,
  });
  return (
    <>
      <DateRangePicker
        value={draft}
        onChange={setDraft}
        locale={definition.locale}
        placeholderFrom={definition.placeholderFrom}
        placeholderTo={definition.placeholderTo}
        cancelLabel={labels.cancel}
        applyLabel={labels.apply}
      />
      <EditorActions
        labels={labels}
        canApply={Boolean(draft.from || draft.to)}
        hasValue={Boolean(value)}
        onApply={() => onApply({ type: 'date-range', ...draft })}
        onClear={onClear}
      />
    </>
  );
}

function MultiSelectFilterEditor({
  definition,
  value,
  labels,
  onApply,
  onClear,
}: {
  definition: DataTableFilterDefinition & { type: 'multi-select' };
  value?: Extract<DataTableFilterValue, { type: 'multi-select' }>;
  labels: DataTableFilterLabels;
  onApply: (value: DataTableFilterValue) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = React.useState(value?.values ?? []);
  return (
    <>
      <MultiSelect
        options={definition.options}
        value={draft}
        onChange={setDraft}
        placeholder={definition.placeholder ?? labels.selectPlaceholder}
        // role=combobox takes no name from content — without this the
        // trigger has no accessible name at all.
        aria-label={definition.placeholder ?? labels.selectPlaceholder}
        selectedText={labels.selected}
        searchable={definition.searchable}
        searchPlaceholder={labels.searchPlaceholder}
        noResultsText={labels.noResults}
        disablePortal
        size="sm"
      />
      <EditorActions
        labels={labels}
        canApply={draft.length > 0}
        hasValue={Boolean(value)}
        onApply={() => onApply({ type: 'multi-select', values: draft })}
        onClear={onClear}
      />
    </>
  );
}

function NumberFilterEditor({
  definition,
  value,
  labels,
  onApply,
  onClear,
}: {
  definition: DataTableFilterDefinition & { type: 'number' };
  value?: Extract<DataTableFilterValue, { type: 'number' }>;
  labels: DataTableFilterLabels;
  onApply: (value: DataTableFilterValue) => void;
  onClear: () => void;
}) {
  const [firstOperator, setFirstOperator] =
    React.useState<DataTableNumberOperator>(value?.first.operator ?? 'gte');
  const [firstValue, setFirstValue] = React.useState(
    value ? String(value.first.value) : '',
  );
  const [secondEnabled, setSecondEnabled] = React.useState(
    Boolean(value?.second),
  );
  const [join, setJoin] = React.useState<'and' | 'or'>(value?.join ?? 'and');
  const [secondOperator, setSecondOperator] =
    React.useState<DataTableNumberOperator>(value?.second?.operator ?? 'lte');
  const [secondValue, setSecondValue] = React.useState(
    value?.second ? String(value.second.value) : '',
  );

  const operatorOptions = (
    Object.keys(labels.operators) as DataTableNumberOperator[]
  ).map((operator) => ({
    value: operator,
    label: labels.operators[operator],
  }));
  const firstNumber = Number(firstValue);
  const secondNumber = Number(secondValue);
  const firstValid = firstValue.trim() !== '' && Number.isFinite(firstNumber);
  const secondValid =
    !secondEnabled ||
    (secondValue.trim() !== '' && Number.isFinite(secondNumber));

  function apply() {
    if (!firstValid || !secondValid) return;
    onApply({
      type: 'number',
      first: { operator: firstOperator, value: firstNumber },
      ...(secondEnabled
        ? {
            join,
            second: { operator: secondOperator, value: secondNumber },
          }
        : {}),
    });
  }

  return (
    <>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] gap-2">
        <Select
          options={operatorOptions}
          value={firstOperator}
          onChange={(next) => setFirstOperator(next as DataTableNumberOperator)}
          disablePortal
          size="sm"
        />
        <Input
          size="sm"
          type="number"
          step={definition.step ?? 'any'}
          value={firstValue}
          onChange={(event) => setFirstValue(event.target.value)}
          placeholder={definition.placeholder}
        />
      </div>

      {secondEnabled ? (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <Select
              options={[
                { value: 'and', label: labels.and },
                { value: 'or', label: labels.or },
              ]}
              value={join}
              onChange={(next) => setJoin(next as 'and' | 'or')}
              disablePortal
              size="sm"
              className="w-28"
            />
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => setSecondEnabled(false)}
            >
              <X />
              {labels.removeCondition}
            </Button>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] gap-2">
            <Select
              options={operatorOptions}
              value={secondOperator}
              onChange={(next) =>
                setSecondOperator(next as DataTableNumberOperator)
              }
              disablePortal
              size="sm"
            />
            <Input
              size="sm"
              type="number"
              step={definition.step ?? 'any'}
              value={secondValue}
              onChange={(event) => setSecondValue(event.target.value)}
              placeholder={definition.placeholder}
            />
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="mt-2"
          onClick={() => setSecondEnabled(true)}
        >
          <Plus />
          {labels.addCondition}
        </Button>
      )}

      <EditorActions
        labels={labels}
        canApply={firstValid && secondValid}
        hasValue={Boolean(value)}
        onApply={apply}
        onClear={onClear}
      />
    </>
  );
}

function formatFilterValue(
  definition: DataTableFilterDefinition,
  value: DataTableFilterValue,
  labels: DataTableFilterLabels,
) {
  if (definition.formatValue) return definition.formatValue(value);
  switch (value.type) {
    case 'text':
      return value.value;
    case 'date-range':
      return [value.from, value.to].filter(Boolean).join(' – ');
    case 'multi-select': {
      if (definition.type !== 'multi-select') {
        return labels.selected(value.values.length);
      }
      const selected = definition.options.filter((option) =>
        value.values.includes(option.value),
      );
      return selected.length <= 2
        ? selected.map((option) => option.label).join(', ')
        : labels.selected(selected.length);
    }
    case 'number': {
      const first = `${labels.operators[value.first.operator]} ${value.first.value}`;
      if (!value.second) return first;
      return `${first} ${value.join === 'or' ? labels.or : labels.and} ${labels.operators[value.second.operator]} ${value.second.value}`;
    }
  }
}
